import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { useSetState } from '@/shared/lib/customHooks';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import formatShippingLabel from '../lib/labelInformation';
import type { LabelInformationProps } from '../ui/labelInformation/labelInformation.types';

/**
 * Backs the label information panel of an RMA ticket, exposing the address formatter, the label download action and the label's display data. Builds the shipping label from the label index and RMA id, derives the hash-prefixed index caption, orders the tracking history by statusDate descending, and keeps the tracking-refresh flag in local state.
 *
 * @param props - Label index, RMA id, tracking history, PDFs and division options.
 * @returns Panel state, the formatters and the ordered tracking history.
 */
const useLabelInformation = (props: LabelInformationProps) => {
  const [state, setState] = useSetState({
    isRefreshingTracking: false,
  });

  /**
   * Renders an RMA shipping label tracking location as one comma-separated address line. Resolves the country and the US or CA state through the option lists on props, appends street1, city, state label and country label only when non-empty, and returns a double-hyphen bullet for a null location.
   *
   * @param location - Location holding street1, city, state and country codes.
   * @returns Comma-joined address string, or a double-hyphen bullet.
   */
  const formatLocation = (location: any) => {
    if (location === null) {
      return DOUBLE_HYPHEN_BULLET;
    }
    const { city, country, state: stateLocation, street1 } = location;
    const newCountry = _.find(props.country, ['value', country]);
    const newState = _.find(country === 'US' ? props.stateUS : props.stateCA, [
      'value',
      stateLocation,
    ]);
    const newLocation = [];
    if (!_.isEmpty(street1)) {
      newLocation.push(street1);
    }
    if (!_.isEmpty(city)) {
      newLocation.push(city);
    }
    if (!_.isEmpty(newState)) {
      newLocation.push(newState.label);
    }
    if (!_.isEmpty(newCountry)) {
      newLocation.push(newCountry.label);
    }
    return _.join(newLocation, ', ');
  };

  /**
   * Saves one of the RMA shipping label PDFs to the user's machine. Takes the buffer at the given index from the filePDF prop and hands it to fileDownload under the supplied filename.
   *
   * @param filename - Name to save the downloaded PDF under.
   * @param index - Position of the PDF within the filePDF array.
   * @returns Resolves once the download has been triggered.
   */
  const onDownloadLink = async (filename: any, index: any) => {
    const { filePDF } = props;
    fileDownload(filePDF?.[index], filename);
  };

  const shippingLabel = formatShippingLabel(props.index, props.rmaId);

  const indexText = `#${props.index + 1}`;

  const orderedTrackingHistory = _.orderBy(props.trackingHistory, ['statusDate'], ['desc']);

  return {
    state,
    setState,
    formatLocation,
    onDownloadLink,
    shippingLabel,
    indexText,
    orderedTrackingHistory,
  };
};

export { useLabelInformation };
